// Static on purpose. This used to be a runtime `import('openapi')` to dodge the
// vitest alias gap, which split the API client into its own chunk that only
// existed at click time: after a deploy, tabs on the old bundle 404'd on it and
// every Download toasted "try again" (Noa, 2026-09-14). The client is already
// in the main bundle; a dynamic import here saved nothing.
import { zipSync } from 'fflate'
import { OpenAPI } from '../../../openapi/core/OpenAPI'
import { request as openApiRequest } from '../../../openapi/core/request'

/**
 * One media file to download: prefer the attachment id (the server signs a URL
 * with Content-Disposition: attachment, so the browser downloads without CORS
 * issues); fall back to the raw URL (may display inline instead).
 */
export interface DownloadableMedia {
  attachmentId?: string | null
  url?: string | null
  /** File kind — picks the extension. */
  kind: 'image' | 'video'
}

/** Hidden-iframe lifetime: long enough for slow signed-URL headers to land. */
const IFRAME_CLEANUP_MS = 2 * 60 * 1000
/**
 * Object URLs stay valid well past any "Save as" dialog: revoking while the
 * browser is still writing the file can truncate it. The blob is bounded by
 * carousel size, so holding it for the rest of a long session is cheap.
 */
const OBJECT_URL_CLEANUP_MS = 10 * 60 * 1000
/**
 * Fetching slides for the zip has a budget: the composer caps the whole
 * download at 30s and toasts a failure past it, so on a slow network we stop
 * zipping in time and hand the slides over one by one (the browser streams
 * those at its own pace) instead of showing "try again" for a download that
 * then lands anyway.
 */
const ZIP_FETCH_BUDGET_MS = 20 * 1000

/**
 * File-system and zip-entry safe: path separators and the characters Windows
 * rejects become `_`, whitespace collapses to `_`. A post named "Menu / July"
 * must not become a folder inside the archive.
 */
export const sanitizeBaseName = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'taka_post'

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
}

/** Extension for a file: the real content type when known, else by kind. */
export function extensionFor(kind: DownloadableMedia['kind'], contentType?: string | null): string {
  const ct = (contentType ?? '').split(';')[0].trim().toLowerCase()
  return EXTENSION_BY_CONTENT_TYPE[ct] ?? (kind === 'video' ? 'mp4' : 'jpg')
}

/** Per-slide filename inside the zip (and for the per-file fallback). */
export function slideFilename(base: string, index: number, ext: string): string {
  return `${base}_${index}.${ext}`
}

/**
 * Build the carousel archive. Slides are stored, not deflated: they are JPEG or
 * MP4 already and re-compressing them only burns CPU on the user's device.
 * Exported for the unit test; the browser path wraps the bytes in a Blob.
 */
export function buildCarouselZip(files: Array<{ name: string; bytes: Uint8Array }>): Uint8Array {
  const entries: Record<string, [Uint8Array, { level: 0 }]> = {}
  for (const f of files) entries[f.name] = [f.bytes, { level: 0 }]
  return zipSync(entries)
}

/**
 * Multi-file downloads must NOT be anchor clicks: an anchor without `target`
 * is a top-level navigation, and the next click cancels the previous one
 * before its headers turn it into a download. On any connection slower than
 * the stagger, only the last slide landed (Matan, 2026-09-15). Hidden iframes
 * navigate independently and leave the page alone.
 */
const downloadViaIframe = (href: string) => {
  const frame = document.createElement('iframe')
  frame.style.display = 'none'
  frame.setAttribute('aria-hidden', 'true')
  frame.src = href
  document.body.appendChild(frame)
  setTimeout(() => frame.remove(), IFRAME_CLEANUP_MS)
}

/** Same-origin blob downloads honour `download`; cross-origin URLs do not. */
const downloadBlob = (blob: Blob, filename: string) => {
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(href), OBJECT_URL_CLEANUP_MS)
}

/** Raw-URL fallback (no attachment id): best effort, may open inline. */
const downloadViaAnchor = (href: string, filename: string) => {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

interface ResolvedFile {
  href: string
  kind: DownloadableMedia['kind']
  /** Set only for raw-URL fallbacks: the anchor needs a name, signed URLs carry theirs. */
  anchorFilename?: string
}

async function resolveSignedUrls(
  base: string,
  items: DownloadableMedia[]
): Promise<ResolvedFile[]> {
  const resolved: ResolvedFile[] = []
  let index = 0
  for (const item of items) {
    index++
    const ext = extensionFor(item.kind)
    const filename = items.length > 1 ? slideFilename(base, index, ext) : `${base}.${ext}`
    if (item.attachmentId) {
      const { url } = await openApiRequest<{ url: string }>(OpenAPI, {
        method: 'GET',
        url: '/v1/media/{id}/download-url',
        path: { id: item.attachmentId },
        query: { filename },
      })
      resolved.push({ href: url, kind: item.kind })
    } else if (item.url) {
      resolved.push({ href: item.url, kind: item.kind, anchorFilename: filename })
    }
  }
  return resolved
}

/**
 * Download a post's media.
 *
 *  - One file (single image, reel, TikTok, Short): one direct download, the
 *    browser streams it from the signed URL.
 *  - Several files (carousel): ONE zip named `<base>.zip` with `<base>_1.jpg`,
 *    `<base>_2.jpg`, ... inside, built in the browser (the media bucket allows
 *    CORS reads). One click, one file, no per-file browser prompts, no race
 *    between nine navigations. If any slide cannot be fetched, every slide is
 *    downloaded as its own file through a hidden iframe instead.
 *
 * Throws when a signed URL cannot be obtained; callers own the error surface.
 */
export async function downloadCreativeMedia(
  baseName: string,
  items: DownloadableMedia[]
): Promise<void> {
  const base = sanitizeBaseName(baseName)
  // Resolve every signed URL FIRST so the download triggers stay as close to
  // the user's click as possible.
  const resolved = await resolveSignedUrls(base, items)
  if (resolved.length === 0) return

  if (resolved.length === 1) {
    const [file] = resolved
    if (file.anchorFilename) downloadViaAnchor(file.href, file.anchorFilename)
    else downloadViaIframe(file.href)
    return
  }

  const abort = new AbortController()
  const budget = setTimeout(
    () => abort.abort(new Error(`zip fetch budget ${ZIP_FETCH_BUDGET_MS}ms exceeded`)),
    ZIP_FETCH_BUDGET_MS
  )
  try {
    const files = await Promise.all(
      resolved.map(async (file, i) => {
        const res = await fetch(file.href, { signal: abort.signal })
        if (!res.ok) throw new Error(`slide ${i + 1}: HTTP ${res.status}`)
        const ext = extensionFor(file.kind, res.headers.get('content-type'))
        return {
          name: slideFilename(base, i + 1, ext),
          bytes: new Uint8Array(await res.arrayBuffer()),
        }
      })
    )
    downloadBlob(new Blob([buildCarouselZip(files)], { type: 'application/zip' }), `${base}.zip`)
  } catch (error) {
    // Slow network past the budget, a CORS surprise, a dead signed URL, a
    // flaky slide: still hand over the files, one download each, without
    // touching the page. Handled degradation, so a log line and not a warn
    // (console.warn is forwarded to error tracking as an alert in this app).
    console.log('[download] carousel zip skipped, per-file fallback:', error)
    abort.abort()
    for (const file of resolved) {
      if (file.anchorFilename) downloadViaAnchor(file.href, file.anchorFilename)
      else downloadViaIframe(file.href)
    }
  } finally {
    clearTimeout(budget)
  }
}

/**
 * Map a creative's `content` blob to its downloadable media. Handles the
 * single-image/video shapes and carousels (`mediaItems`). Returns [] when
 * there is nothing to download.
 */
export function collectDownloadableMedia(content: unknown): DownloadableMedia[] {
  if (!content || typeof content !== 'object') return []
  const record = content as Record<string, unknown>
  const mediaItems = record.mediaItems as
    Array<{ attachmentId?: string; url?: string; type?: string }> | undefined
  if (Array.isArray(mediaItems) && mediaItems.length > 0) {
    const slides = mediaItems
      .filter(item => item.attachmentId || item.url)
      .map(item => ({
        attachmentId: item.attachmentId,
        url: item.url,
        kind: item.type === 'video' ? ('video' as const) : ('image' as const),
      }))
    // All slides unresolvable → fall through to the top-level fields rather
    // than reporting "nothing to download" for a post with a hero image.
    if (slides.length > 0) return slides
  }
  const videoAttachmentId = record.videoAttachmentId as string | null | undefined
  const videoUrl = record.videoUrl as string | null | undefined
  if (videoAttachmentId || videoUrl) {
    return [{ attachmentId: videoAttachmentId, url: videoUrl, kind: 'video' }]
  }
  const imageAttachmentId = record.imageAttachmentId as string | null | undefined
  const imageUrl = record.imageUrl as string | null | undefined
  if (imageAttachmentId || imageUrl) {
    return [{ attachmentId: imageAttachmentId, url: imageUrl, kind: 'image' }]
  }
  return []
}
